import { type FC, useMemo } from 'react'
import { SelectDropdown, type SelectOption } from '../../../../components/SelectDropdown/SelectDropdown'
import { MeetingMode, type SchedulingEventType } from '../../../../models/Scheduling'
import { SchedulingDropdownItem } from './SchedulingDropdownItem'

interface EventTypeSelectProps {
  eventTypes: SchedulingEventType[]
  selectedEventTypeId: string | null
  onEventTypeChange: (eventType: SchedulingEventType) => void
}

interface MeetingModeLabelProps {
  meetingMode: MeetingMode
}

const MeetingModeLabel: FC<MeetingModeLabelProps> = ({ meetingMode }) =>
  meetingMode === MeetingMode.IN_PERSON ? (
    <>
      <i className='bi bi-people' aria-hidden='true' /> In person
    </>
  ) : (
    <>
      <i className='bi bi-camera-video' aria-hidden='true' /> Virtual
    </>
  )

export const EventTypeSelect: FC<EventTypeSelectProps> = ({ eventTypes, selectedEventTypeId, onEventTypeChange }) => {
  const activeEventTypes = useMemo(() => eventTypes.filter((eventType) => eventType.active), [eventTypes])
  const options: SelectOption[] = useMemo(
    () =>
      activeEventTypes.map((eventType) => ({
        id: eventType.id,
        label: `${eventType.name} · ${eventType.durationMinutes} min · ${eventType.meetingMode === MeetingMode.IN_PERSON ? 'In person' : 'Virtual'}`,
        displayLabel: (
          <span className='d-flex flex-column'>
            <span className='d-inline-flex align-items-center zocks-gap-0.25'>
              {eventType.name} · {eventType.durationMinutes} min · <MeetingModeLabel meetingMode={eventType.meetingMode} />
            </span>
            {eventType.description ? <span className='paragraph-small zocks-text-gray-500'>{eventType.description}</span> : null}
          </span>
        ),
      })),
    [activeEventTypes],
  )

  const selected = options.find((option) => option.id === selectedEventTypeId) ?? null

  return (
    <div className='d-flex flex-column zocks-gap-0.25'>
      <label className='bold'>Event type</label>
      <div className='paragraph-small zocks-text-gray-600'>Sets meeting length, availability and place</div>
      <SelectDropdown
        options={options}
        initialSelectedOption={selected}
        placeholder='Select event type'
        DropDownItemOptionComponent={SchedulingDropdownItem}
        onOptionSelected={(option) => {
          const eventType = activeEventTypes.find((candidate) => candidate.id === option.id)
          if (eventType) {
            onEventTypeChange(eventType)
          }
        }}
      />
    </div>
  )
}
